import React from "react";

const statuses = {
  observed: "Observed evidence",
  stale: "Older evidence - revalidation required",
  user_provided: "Your input - not independently verified",
  assumed: "Assumption",
  calculated: "Calculated scenario",
  unavailable: "Not assessed",
};

const aud = new Intl.NumberFormat("en-AU", {
  style: "currency",
  currency: "AUD",
  maximumFractionDigits: 0,
});

export default function EvidenceReading({ report, metrics: presentedMetrics = [] }) {
  const reading = report.explanation;
  if (!reading || reading.evidence_id !== report.evidence_id) return null;
  const metrics = new Map(
    report.evidence.metrics.map((metric) => [metric.id, metric]),
  );
  presentedMetrics.forEach((metric) => metrics.set(metric.id, metric));
  // Do not display a sentence if its citation no longer resolves in this report.
  if (!reading.entries.every((entry) => metrics.has(entry.metric_id)))
    return null;
  return (
    <section
      className="research-card evidence-reading"
      aria-label="Evidence reading"
    >
      <h3>What this evidence means</h3>
      <p>
        Prepared from the cited evidence using fixed wording. This is not an
        AI-generated assessment.
      </p>
      <p className="notice">{reading.coverage}</p>
      <ul className="reading-list">
        {reading.entries.map((entry) => {
          const metric = metrics.get(entry.metric_id);
          const isAskingPrice =
            metric.id === "asking_price" &&
            metric.unit === "AUD" &&
            typeof metric.value === "number";
          const entryText = isAskingPrice
            ? `The entered price is ${aud.format(metric.value)}.`
            : metric.id === "rent" && metric.status !== "unavailable"
              ? `The postcode weekly rent benchmark is ${aud.format(metric.value)} per week.`
            : entry.text;
          return (
            <li key={entry.metric_id}>
              <p>{entryText}</p>
              {entry.caveats.map((caveat) => (
                <p className="source" key={caveat}>
                  {caveat}
                </p>
              ))}
              <details>
                <summary>Evidence: {metric.label}</summary>
                <p className="source">
                  {metric.id} · {statuses[metric.status]}
                </p>
                {metric.status !== "unavailable" && (
                  <p>
                    {isAskingPrice
                      ? aud.format(metric.value)
                      : `${metric.value} ${metric.unit || ""}`.trim()}
                  </p>
                )}
                {metric.geography_type && (
                  <p>
                    Scope: {metric.geography_type} {metric.geography_id}
                  </p>
                )}
                {metric.observation_period && (
                  <p>Observation period: {metric.observation_period}</p>
                )}
                {metric.source_name && <p>Source: {metric.source_name}</p>}
                {metric.derivation && <p>{metric.derivation}</p>}
                {metric.licence && (
                  <p className="source">
                    {metric.attribution} · {metric.licence}
                  </p>
                )}
                {metric.source_url &&
                  /^https?:\/\//i.test(metric.source_url) && (
                    <a href={metric.source_url}>View cited source</a>
                  )}
              </details>
            </li>
          );
        })}
      </ul>
      <p className="source">Reading version: {reading.version}</p>
      <p>
        Financial scenarios are explained separately in their calculation
        details. General information only, not financial, credit, legal or tax
        advice.
      </p>
    </section>
  );
}
